<!-- eslint-disable -->
<template>
  <div class="crm-workspace">
    <!-- Header Section -->
    <header class="crm-header shadow-sm">
      <div class="crm-header__glow" />
      <div class="crm-header__content">
        <div class="d-flex align-items-center">
          <div class="crm-header__icon mr-2">
            <feather-icon icon="ActivityIcon" size="28" />
          </div>
          <div>
            <h1 class="crm-header__title">Opportunities Pipeline</h1>
            <p class="crm-header__subtitle">Customers follow-up, new clients and support. Each card is an opportunity for a client; each status has its own steps.</p>
          </div>
        </div>

        <div class="crm-header__actions mt-2 mt-lg-0">
          <!-- View Toggle -->
          <div class="crm-view-switch mr-2">
            <button
              type="button"
              class="crm-view-switch__btn"
              :class="{ 'crm-view-switch__btn--active': viewMode === 'kanban' }"
              @click="viewMode = 'kanban'"
              title="Kanban Board"
            >
              <feather-icon icon="GridIcon" size="16" />
              <span>Kanban</span>
            </button>
            <button
              type="button"
              class="crm-view-switch__btn"
              :class="{ 'crm-view-switch__btn--active': viewMode === 'list' }"
              @click="switchToList"
              title="List View"
            >
              <feather-icon icon="ListIcon" size="16" />
              <span>List</span>
            </button>
          </div>

          <!-- My pipeline toggle — only managers can widen beyond their own work -->
          <button
            v-if="access.canViewTeam"
            type="button"
            class="crm-btn mr-2"
            :class="onlyMine ? 'crm-btn--primary' : 'crm-btn--secondary'"
            :title="onlyMine ? 'Showing only my items' : 'Showing all items'"
            @click="onlyMine = !onlyMine"
          >
            <feather-icon icon="UserIcon" size="16" />
            <span>{{ onlyMine ? 'My Opportunities' : 'All' }}</span>
          </button>

          <!-- Add Button -->
          <button
            type="button"
            class="crm-btn crm-btn--primary mr-2"
            @click="showAddModal = true"
          >
            <feather-icon icon="PlusIcon" size="16" />
            <span>Add Item</span>
          </button>

          <!-- Manage Stages Button (Only for New Deals) -->
          <button
            v-if="activePipeline === 'deals'"
            type="button"
            class="crm-btn crm-btn--secondary"
            @click="showStageModal = true"
          >
            <feather-icon icon="SettingsIcon" size="16" />
            <span>Manage Stages</span>
          </button>

          <b-button
            variant="outline-primary"
            size="sm"
            @click="showSettingsModal = true"
          >
            <feather-icon icon="SettingsIcon" class="mr-50" />
            Board Settings
          </b-button>
        </div>
      </div>

      <!-- Pipeline Segmented Selector Nav -->
      <nav class="crm-nav">
        <button
          v-for="pipe in pipelines"
          :key="pipe.id"
          type="button"
          class="crm-nav__tab"
          :class="{ 'crm-nav__tab--active': activePipeline === pipe.id }"
          @click="activePipeline = pipe.id"
        >
          <feather-icon :icon="pipe.icon" size="18" class="mr-1" />
          <span>{{ pipe.name }}</span>
          <span
            class="crm-nav__badge"
            :class="pipe.badgeClass"
          >
            {{ getPendingCount(pipe.id) }}
          </span>
        </button>
      </nav>
    </header>

    <!-- Toolbar: Search, Filters, Bulk Actions & Settings Trigger -->
    <pipeline-toolbar
      :search-query.sync="searchQuery"
      :filters="filters"
      :stages="activeStages"
      :selected-count="selectedCardIds.length"
      :board-settings="boardSettings"
      @toggle-filter-panel="showFilterPanel = true"
      @open-settings="showSettingsModal = true"
      @density-change="boardSettings.density = $event"
      @filter-clear-key="clearFilterKey"
      @bulk-action="handleBulkAction"
      @export="exportData"
    />

    <!--
      Karim, 3 Sep: "from inside the client itself, I can go to the
      opportunity." Arrived here filtered to one client — say so, with a way
      back to the whole board, or the filter would look like a bug.
    -->
    <div
      v-if="clientIdFilter"
      class="crm-client-filter-banner"
    >
      <feather-icon icon="UserIcon" size="14" />
      <span>Showing opportunities for <strong>{{ $route.query.clientName || ('client #' + clientIdFilter) }}</strong></span>
      <button
        type="button"
        class="crm-client-filter-banner__clear"
        @click="clearClientFilter"
      >
        <feather-icon icon="XIcon" size="13" />
        Show all
      </button>
    </div>

    <!--
      SELECTION BAR — Karim, 16 Aug: "multiselect and take action like assign and so."

      Shown to EVERYONE with cards ticked, not just managers. A rep closing five dead
      renewals one dialog at a time is the friction that makes people stop using the
      board. Reassign stays manager-only (that is someone else's book), but recording
      an outcome on your own work is your job. The server checks ownership per card
      and reports how many it left alone, so a rep who ticks a colleague's card gets
      told rather than silently changing it.
    -->
    <div v-if="selectedCardIds.length" class="crm-reassign-bar mx-3 mt-2">
      <span class="crm-reassign-bar__count">
        <feather-icon icon="CheckSquareIcon" size="15" class="mr-05" />
        {{ selectedCardIds.length }} selected
      </span>

      <template v-if="access.canViewTeam">
        <select v-model="reassignTo" class="crm-reassign-bar__select">
          <option :value="null">— reassign to —</option>
          <option v-for="u in team" :key="u.id" :value="u.id">{{ memberLabel(u) }}</option>
        </select>
        <button
          type="button"
          class="crm-btn crm-btn--primary"
          :disabled="!reassignTo || reassigning"
          @click="reassignSelected"
        >
          <feather-icon icon="UserCheckIcon" size="15" />
          <span>{{ reassigning ? 'Reassigning…' : 'Reassign' }}</span>
        </button>
      </template>

      <button
        type="button"
        class="crm-btn crm-btn--success"
        :disabled="bulkActionBusy"
        @click="bulkOutcome(true)"
      >
        <feather-icon icon="CheckCircleIcon" size="15" />
        <span>{{ activePipeline === 'renewals' ? 'Renewed' : 'Won' }}</span>
      </button>
      <button
        type="button"
        class="crm-btn crm-btn--danger"
        :disabled="bulkActionBusy"
        @click="askLostReason = !askLostReason"
      >
        <feather-icon icon="XCircleIcon" size="15" />
        <span>{{ negativeLabel }}</span>
      </button>

      <!--
        A reason is REQUIRED, so it is asked for inline rather than after the fact.
        The list differs per pipeline: you lose a lead for different reasons than an
        existing client declines a renewal, and merging the two would make the
        analysis in Targets → "Why we lose" meaningless.
      -->
      <template v-if="askLostReason">
        <select v-model="lostReason" class="crm-reassign-bar__select">
          <option value="">— reason (required) —</option>
          <option v-for="r in negativeReasons" :key="r" :value="r">{{ r }}</option>
        </select>
        <input
          v-if="lostReason === 'Other'"
          v-model="lostReasonOther"
          class="crm-reassign-bar__select"
          placeholder="Say what happened…"
        >
        <button
          type="button"
          class="crm-btn crm-btn--danger"
          :disabled="!resolvedLostReason || bulkActionBusy"
          @click="confirmNegativeOutcome"
        >
          {{ bulkActionBusy ? 'Saving…' : `Confirm ${negativeLabel}` }}
        </button>
      </template>

      <select
        class="crm-reassign-bar__select"
        :disabled="bulkActionBusy"
        @change="bulkPriority($event.target.value); $event.target.value = ''"
      >
        <option value="">— priority —</option>
        <option value="High">High</option>
        <option value="Medium">Medium</option>
        <option value="Low">Low</option>
      </select>

      <button
        type="button"
        class="crm-btn crm-btn--danger"
        :disabled="bulkActionBusy"
        @click="bulkDeleteSelected"
      >
        <feather-icon icon="Trash2Icon" size="15" />
        <span>Delete</span>
      </button>

      <button type="button" class="crm-btn crm-btn--secondary" @click="clearSelection">Clear</button>
    </div>


    <!-- Notice Alert -->
    <div v-if="apiError" class="alert alert-info mx-3 mt-3 d-flex align-items-center" role="alert">
      <feather-icon icon="AlertTriangleIcon" size="20" class="mr-2" />
      <!-- Honesty fix: there is no simulated data any more — the board shows
           real cards from the CRM backend, or nothing. Do not claim otherwise. -->
      <div>
        <strong>Notice:</strong> {{ apiError }}
      </div>
      <button type="button" class="close ml-auto" @click="apiError = null">
        <span aria-hidden="true">&times;</span>
      </button>
    </div>

    <!-- Main Content Area -->
    <main class="crm-content">
      <!-- Loading Indicator -->
      <div v-if="loading" class="crm-loading py-5 text-center">
        <div class="spinner-border text-primary" role="status">
          <span class="sr-only">Loading...</span>
        </div>
        <p class="mt-2 text-muted">Loading pipeline...</p>
      </div>

      <!-- Content Area -->
      <div v-else>
        <!-- KANBAN BOARD VIEW -->
        <kanban-board
          v-if="viewMode === 'kanban'"
          :stages="activeStages"
          :cards="filteredCards"
          :active-pipeline="activePipeline"
          :board-settings="boardSettings"
          :bulk-mode="selectedCardIds.length > 0"
          :selected-card-ids="selectedCardIds"
          :draggable-columns="activePipeline === 'deals'"
          :stage-counts="activeStageCounts"
          :stage-loading="stageLoading"
          @card-moved="handleCardMoved"
          @card-click="openDetail"
          @card-quick-action="handleQuickAction"
          @card-select="toggleCardSelect"
          @stage-reorder="handleStageReorder"
          @load-more="loadMoreStage"
        />

        <!-- LIST TABLE VIEW -->
        <list-view
          v-else
          :cards="filteredCards"
          :stages="activeStages"
          :active-pipeline="activePipeline"
          :board-settings="boardSettings"
          :bulk-mode="true"
          :selected-card-ids="selectedCardIds"
          @card-click="openDetail"
          @card-delete="deleteCard"
          @stage-change="handleStageSelectChange"
          @card-select="toggleCardSelect"
          @select-all="selectAllCards"
          @clear-selection="selectedCardIds = []"
        />
      </div>
    </main>

    <!-- MODALS & DRAWERS -->
    <filter-panel
      :visible="showFilterPanel"
      :filters="filters"
      :stages="activeStages"
      :owners="teamNames"
      :labels="availableLabels"
      @close="showFilterPanel = false"
      @filter-change="handleFilterChange"
      @clear-all="clearAllFilters"
    />

    <!--
      MID-CRM P1.2 / P2.1: the drawer was never given a team, so its "Add assignee"
      list was ALWAYS empty. It now receives the real team from GET crm/team, and
      only managers may reassign (BRD §8.1). checklist/time-entry/watcher listeners
      are gone with the features that had no storage behind them.
    -->
    <card-detail-drawer
      :card="selectedCard"
      :stages="activeStages"
      :active-pipeline="activePipeline"
      :current-user-name="currentUserName"
      :available-labels="availableLabels"
      :team="team"
      :can-assign="access.canViewTeam"
      @close="selectedCard = null"
      @stage-change="handleStageSelectChange"
      @field-update="handleFieldUpdate"
      @comment-add="handleCommentAdd"
      @label-add="handleLabelAdd"
      @label-remove="handleLabelRemove"
      @owner-change="handleOwnerChange"
      @certificate-linked="handleCertificateLinked"
      @card-refreshed="handleCardRefreshed"
      @assignee-add="handleAssigneeAdd"
      @assignee-remove="handleAssigneeRemove"
      @product-update="handleProductUpdate"
      @delete="deleteCard"
    />

    <card-add-modal
      :visible="showAddModal"
      :active-pipeline="activePipeline"
      :active-pipeline-name="activePipelineName"
      :access="access"
      @close="showAddModal = false"
      @submit="submitAddCard"
    />

    <decision-modal
      :visible="showDecisionModal"
      :renewed-reasons="renewedReasons"
      :not-renewed-reasons="notRenewedReasons"
      @close="cancelDecision"
      @submit="submitDecision"
    />

    <stage-manager-modal
      :visible="showStageModal"
      :stages="editableStages"
      @close="showStageModal = false"
      @stage-add="createStage"
      @stage-delete="deleteStage"
      @stage-update="updateStage"
      @stage-reorder="reorderStage"
    />

    <board-settings
      :visible="showSettingsModal"
      :settings="boardSettings"
      @close="showSettingsModal = false"
      @settings-change="updateBoardSetting"
    />

    <activity-modal
      :visible="showActivityModal"
      :card="activityCard"
      :target-stage="pendingTargetStage"
      :start-in-email-mode="startInEmailMode"
      @close="closeActivityModal"
      @submit="submitActivityModal"
      @send-email="submitCardEmail"
    />
  </div>
</template>

<script>
/* eslint-disable */
import axios from 'axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import decryptData from '@/store/auth/decryptData'

import {
  RENEWAL_STAGES,
  DEAL_STAGES_DEFAULT,
  TICKET_STAGES,
  RENEWED_REASONS,
  NOT_RENEWED_REASONS,
  LOST_REASONS,
  DEFAULT_LABELS,
  CONCERN_LABEL,
  DEFAULT_BOARD_SETTINGS,
  DEFAULT_FILTERS
} from './pipeline-constants'

import KanbanBoard from './components/KanbanBoard.vue'
import PipelineToolbar from './components/PipelineToolbar.vue'
import ListView from './components/ListView.vue'
import FilterPanel from './components/FilterPanel.vue'
import CardDetailDrawer from './components/CardDetailDrawer.vue'
import CardAddModal from './components/CardAddModal.vue'
import DecisionModal from './components/DecisionModal.vue'
import StageManagerModal from './components/StageManagerModal.vue'
import BoardSettings from './components/BoardSettings.vue'
import ActivityModal from './components/ActivityModal.vue'

export default {
  name: 'PipelineCRM',
  components: {
    KanbanBoard,
    PipelineToolbar,
    ListView,
    FilterPanel,
    CardDetailDrawer,
    CardAddModal,
    DecisionModal,
    StageManagerModal,
    BoardSettings,
    ActivityModal
  },
  data() {
    return {
      activePipeline: 'renewals',
      viewMode: 'kanban',
      loading: false,
      apiError: null,
      searchQuery: '',
      // Karim, 3 Sep: "from inside the client itself, I can go to the
      // opportunity" — set from ?clientId= when arriving from a contact.
      clientIdFilter: '',

      // Modal & Drawer States
      showAddModal: false,
      showStageModal: false,
      showDecisionModal: false,
      showFilterPanel: false,
      showSettingsModal: false,
      showActivityModal: false,
      activityCard: null,
      pendingTargetStage: null,
      startInEmailMode: false,
      selectedCard: null,

      // Bulk actions selection
      selectedCardIds: [],

      // Audit A-1: per-pipeline per-stage server totals + load-more spinners.
      // The board no longer pretends 2000 cards are the whole pipeline.
      stageCounts: { renewals: {}, deals: {}, tickets: {} },
      stageLoading: {},
      stagePageSize: 50,
      // Never let a stalled request leave the board spinning forever.
      requestTimeout: 20000,

      // User details. No hardcoded fallback name: it used to default to 'Khaled', so
      // anything the user created before /crm/access answered was stamped with a
      // colleague's name. Empty until the server says who this is.
      currentUserName: localStorage.getItem('name') || '',

      // Pipelines Metadata
      pipelines: [
        { id: 'renewals', name: 'Customers Follow-up', icon: 'AwardIcon', badgeClass: 'crm-nav__badge--warning' },
        { id: 'deals', name: 'Opportunities', icon: 'TrendingUpIcon', badgeClass: 'crm-nav__badge--primary' }
        // Audit P1-5: Tickets tab hidden until its requirements exist.
        // Re-add when ready: { id: 'tickets', name: 'Support & Tickets', icon: 'LifeBuoyIcon', badgeClass: 'crm-nav__badge--success' }
      ],

      // Stages
      renewalStages: RENEWAL_STAGES,
      ticketStages: TICKET_STAGES,
      dealStages: DEAL_STAGES_DEFAULT,

      // Pipeline Data
      renewalsList: [],
      dealsList: [],
      ticketsList: [],

      // "My pipeline" toggle — a salesperson opens the board on their own work
      onlyMine: false,
      // Who am I / what may I see (from crm/me)
      access: { scope: 'own', canViewTeam: false, seesAll: false, name: null },
      team: [],
      reassignTo: null,
      // MID-CRM 5.4 — bulk assign by FILTER, not by page.
      bulkActionBusy: false,
      askLostReason: false,
      lostReason: '',
      lostReasonOther: '',
      reassigning: false,

      // Decision State
      decisionCard: null,
      renewedReasons: RENEWED_REASONS,
      notRenewedReasons: NOT_RENEWED_REASONS,

      // Metadata Constants
      availableLabels: DEFAULT_LABELS,
      // MID-CRM P1.2 — `teamMembers: MOCK_TEAM_MEMBERS` (Mohammed, Sara, Khaled…)
      // deleted. It fed the drawer's assignee picker AND the board's owner filter,
      // so filtering by owner could never match a real card. Both now use the real
      // team from GET crm/team (see the `team` array and `teamNames`).
      boardSettings: { ...DEFAULT_BOARD_SETTINGS },
      filters: { ...DEFAULT_FILTERS }
    }
  },
  computed: {
    activeStages() {
      if (this.activePipeline === 'renewals') return this.renewalStages
      if (this.activePipeline === 'tickets') return this.ticketStages
      return this.dealStages
    },
    // Stages editable through the Stage Manager (opportunities + deals)
    editableStages() {
      return this.dealStages
    },
    activePipelineName() {
      if (this.activePipeline === 'renewals') return 'Customer Follow-up'
      if (this.activePipeline === 'tickets') return 'Support Ticket'
      return 'New Client Opportunity'
    },
    activeCardList() {
      if (this.activePipeline === 'renewals') return this.renewalsList
      if (this.activePipeline === 'tickets') return this.ticketsList
      return this.dealsList
    },
    // Audit A-1: server totals for the active pipeline's stages
    activeStageCounts() {
      return this.stageCounts[this.activePipeline] || {}
    },
    /** MID-CRM P1.2 — real salesperson names for the board's owner filter. */
    teamNames() {
      return (this.team || []).map(u => u.name).filter(Boolean)
    },
    /**
     * MID-CRM (Karim, 16 Aug) — search and filters are applied by the SERVER now, so
     * this is a pass-through.
     *
     * It used to filter this array, which only ever holds the 50 cards per column the
     * board had loaded. Searching a client name that sat on card 51 returned nothing
     * and the rep concluded the client was not in the CRM. Re-adding any filter here
     * would silently re-create that bug — change applyBoardFilters() in
     * CrmPipelineController instead, and add the parameter to boardFilterParams().
     */
    filteredCards() {
      return this.activeCardList
    },

    /* Losing a NEW client is "Lost"; an EXISTING client declining is "Rejected". */
    negativeLabel() {
      return this.activePipeline === 'renewals' ? 'Rejected' : 'Lost'
    },

    negativeReasons() {
      return this.activePipeline === 'renewals' ? NOT_RENEWED_REASONS : LOST_REASONS
    },

    resolvedLostReason() {
      const r = this.lostReason === 'Other' ? this.lostReasonOther : this.lostReason
      return (r || '').trim()
    }
  },
  watch: {
    renewalsList: { handler: 'saveLocalState', deep: true },
    dealsList: { handler: 'saveLocalState', deep: true },
    ticketsList: { handler: 'saveLocalState', deep: true },
    dealStages: { handler: 'saveLocalState', deep: true },
    boardSettings: { handler: 'saveLocalState', deep: true },

    // Search and filters now run on the server, so a change means RE-FETCH, not
    // re-filter. Debounced: a rep types faster than the board can load.
    searchQuery() { this.reloadBoardDebounced() },
    onlyMine() { this.reloadBoardDebounced() },
    filters: { deep: true, handler() { this.reloadBoardDebounced() } },
  },
  mounted() {
    // Karim, 3 Sep: "from inside the client itself, I can go to the
    // opportunity" — arriving from a contact's "N open opportunities" link.
    if (this.$route.query.clientId) this.clientIdFilter = String(this.$route.query.clientId)
    this.initData()
  },
  methods: {
    /** Karim, 2 Sep: show role + office next to each name when assigning. */
    memberLabel(member) {
      if (!member) return ''
      // Karim, 3 Sep: "should be markter name - office" — crm/team now
      // defaults to markter-only, so the role parenthetical was just noise
      // repeating "(markter)" on every row. Still shown for the rare ?all=1
      // case (a genuinely mixed-role list) where it's actually informative.
      const office = member.officeName || 'No office'
      const roles = Array.isArray(member.roles) ? member.roles : []
      const mixedRoles = roles.length && !(roles.length === 1 && roles[0] === 'markter')
      return mixedRoles ? `${member.name} (${roles.join('/')}) — ${office}` : `${member.name} — ${office}`
    },
    async initData() {
      this.loading = true
      this.apiError = null
      
      // Aggressively wipe legacy fake data stuck in browser cache
      localStorage.removeItem('crm_renewals')
      localStorage.removeItem('crm_deals')
      localStorage.removeItem('crm_tickets')
      
      this.loadLocalState()

      // Safe background fetch of live API data.
      // Hard-capped: a hung request must never leave the user staring at a
      // spinner with an empty board (it is better to show what did arrive).
      try {
        // fetchAccess is inside the guarded race too — when it stalled, the
        // board used to spin forever with nothing on screen.
        const work = (async () => {
          await this.fetchAccess()
          if (this.access.canViewTeam) this.fetchTeam()
          await Promise.allSettled([
            this.fetchDealStages(),
            this.fetchBackendRenewals(),
            this.fetchBackendDeals()
          ])
        })()
        const guard = new Promise(resolve => setTimeout(() => resolve('timeout'), this.requestTimeout + 5000))
        if (await Promise.race([work, guard]) === 'timeout') {
          console.warn('Board load exceeded the time budget — showing what loaded so far.')
          this.apiError = 'The board took too long to load. Some cards may be missing — press Refresh to retry.'
        }
      } catch (err) {
        console.warn('Backend sync notice:', err)
      } finally {
        this.loading = false
        this.openFromQuery()
      }
    },

    /**
     * Deep link from My Day: /crm-system/pipeline?card=<id>&pipeline=<name>
     * Switches to the right board and opens that opportunity straight away.
     */
    openFromQuery() {
      const { card, pipeline } = this.$route.query || {}
      if (!card) return
      if (pipeline && this.pipelines.some(p => p.id === pipeline)) {
        this.activePipeline = pipeline
      }
      this.$nextTick(() => {
        const found = this.activeCardList.find(c => String(c.id) === String(card))
        if (found) {
          this.openDetail(found)
        } else {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Not found',
              icon: 'AlertCircleIcon',
              variant: 'primary',
              text: `Opportunity ${card} is not in this board (it may be assigned to someone else).`
            }
          })
        }
      })
    },

    saveLocalState() {
      localStorage.setItem('crm_deal_stages', JSON.stringify(this.dealStages))
      localStorage.setItem('crm_board_settings', JSON.stringify(this.boardSettings))
    },

    loadLocalState() {
      const s = localStorage.getItem('crm_deal_stages')
      const b = localStorage.getItem('crm_board_settings')

      if (s) this.dealStages = JSON.parse(s)
      if (b) this.boardSettings = JSON.parse(b)

      return true
    },

    async fetchAccess() {
      try {
        const res = await axios.get('crm/me', { timeout: this.requestTimeout })
        const body = decryptData(res.data.payload)
        if (body && body.data) {
          this.access = body.data
          if (body.data.name) this.currentUserName = body.data.name
        }
      } catch (e) { /* stay with the safe default: own only */ }
    },

    async fetchTeam() {
      try {
        const res = await axios.get('crm/team')
        const body = decryptData(res.data.payload)
        this.team = (body && body.data) || []
      } catch (e) { /* optional */ }
    },

    /** Won / Renewed — no reason needed, though one may be recorded from the card. */
    async bulkOutcome(won) {
      if (!this.selectedCardIds.length) return
      await this.runBulkAction({ action: won ? 'won' : 'lost' })
    },

    /**
     * Lost / Rejected. The reason is mandatory — the server refuses without it too,
     * so this is convenience, not security. It is the one field that makes the
     * outcome analysis worth collecting.
     */
    async confirmNegativeOutcome() {
      if (!this.resolvedLostReason || !this.selectedCardIds.length) return
      await this.runBulkAction({ action: 'lost', reason: this.resolvedLostReason })
      this.askLostReason = false
      this.lostReason = ''
      this.lostReasonOther = ''
    },

    clearSelection() {
      this.selectedCardIds = []
      this.askLostReason = false
      this.lostReason = ''
      this.lostReasonOther = ''
    },

    async bulkPriority(priority) {
      if (!priority || !this.selectedCardIds.length) return
      // `toPriority`, not `priority`: the server reads `priority` as a filter.
      await this.runBulkAction({ action: 'priority', toPriority: priority })
    },

    /** Karim, 2 Sep: "select all filtered... assign, delete." Same guard as the
     * single-card deleteCard() — the server only acts on what canWriteOwned lets
     * this user touch, and reports the rest as skipped. */
    bulkDeleteSelected() {
      if (!this.selectedCardIds.length) return
      const count = this.selectedCardIds.length
      this.$swal({
        title: `Delete ${count} opportunit${count === 1 ? 'y' : 'ies'}?`,
        text: 'Permanently removes them from storage.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Delete',
        customClass: { confirmButton: 'btn btn-danger', cancelButton: 'btn btn-outline-secondary ml-1' }
      }).then(async res => {
        if (res.isConfirmed) await this.runBulkAction({ action: 'delete' })
      })
    },

    async runBulkAction(payload) {
      this.bulkActionBusy = true
      try {
        const res = await axios.post(`crm/${this.activePipeline}/bulk-action`, {
          cardIds: this.selectedCardIds,
          ...payload,
        })
        const body = decryptData(res.data.payload)
        const data = (body && body.data) || {}
        const skipped = data.skippedNotYours || 0

        this.$toast({
          component: ToastificationContent,
          props: {
            title: payload.action === 'delete' ? 'Deleted' : 'Updated',
            icon: 'CheckIcon',
            variant: skipped ? 'info' : 'success',
            // Say what was skipped. Silently ignoring a rep's selection is how
            // people conclude the board "doesn't work".
            text: `${data.changed || 0} ${payload.action === 'delete' ? 'removed' : 'updated'}`
              + (skipped ? ` — ${skipped} belong to someone else and were left alone` : ''),
          },
        })

        this.selectedCardIds = []
        await this.initData()
      } catch (e) {
        const msg = (e.response && e.response.status === 422)
          ? 'The server rejected the change — check the reason field.'
          : 'Could not update the opportunities.'
        this.$toast({
          component: ToastificationContent,
          props: { title: 'Not updated', icon: 'AlertCircleIcon', variant: 'danger', text: msg },
        })
      } finally {
        this.bulkActionBusy = false
      }
    },

    async reassignSelected() {
      if (!this.reassignTo || !this.selectedCardIds.length) return
      this.reassigning = true
      try {
        const res = await axios.post(`crm/${this.activePipeline}/reassign`, {
          cardIds: this.selectedCardIds,
          ownerId: this.reassignTo
        })
        const body = decryptData(res.data.payload)
        const owner = (body && body.data && body.data.owner) || 'the salesperson'
        this.$toast({
          component: ToastificationContent,
          props: { title: 'Reassigned', icon: 'CheckIcon', variant: 'success', text: `${this.selectedCardIds.length} opportunities moved to ${owner}` }
        })
        this.selectedCardIds = []
        this.reassignTo = null
        await this.initData()
      } catch (e) {
        this.$toast({
          component: ToastificationContent,
          props: { title: 'Failed', icon: 'AlertCircleIcon', variant: 'danger', text: 'Could not reassign the opportunities' }
        })
      } finally {
        this.reassigning = false
      }
    },

    // New Deals stages come from the backend so custom stages + sub-steps persist.
    async fetchDealStages() {
      try {
        const res = await axios.get('crm/stages', { params: { pipeline: 'deals' } })
        const body = decryptData(res.data.payload)
        if (body && body.success && Array.isArray(body.data) && body.data.length) {
          this.dealStages = body.data
        }
      } catch (e) {
        // keep the built-in defaults
      }
    },

    // Persist a single card (any pipeline) to the backend.
    async persistCard(card, pipeline) {
      try {
        await axios.post(`crm/${pipeline || this.activePipeline}`, card)
      } catch (e) {
        // non-blocking
      }
    },

    // ── Audit A-1: paginated board loading ────────────────────────────────
    // The server caps any single fetch at 2000 cards; Renewals alone has
    // 4,369. Loading "everything" therefore silently hid over half the
    // pipeline. Instead: fetch per-stage totals, then a first window per
    // stage, and let each column "Load more".

    /**
     * Returns the per-stage totals, or null when the endpoint is unavailable
     * (e.g. the backend has not been restarted with the counts route yet).
     * Null is the signal to fall back to the single unfiltered fetch.
     */
    async fetchStageCounts(pipeline) {
      try {
        const res = await axios.get(`crm/${pipeline}/counts`, {
          // Same filters as the list, or the column count contradicts its contents.
          params: this.boardFilterParams(),
          timeout: this.requestTimeout,
        })
        const body = decryptData(res.data.payload)
        if (body && body.success && body.data && body.data.counts) {
          const counts = body.data.counts
          this.$set(this.stageCounts, pipeline, counts)
          return counts
        }
      } catch (e) {
        // Non-blocking — the board falls back to loading the pipeline in one go.
        console.warn(`No counts endpoint for ${pipeline} (falling back):`, e && e.message)
      }
      return null
    },

    /**
     * MID-CRM (Karim, 16 Aug): search and filters must run on the SERVER. The board
     * only holds 50 cards per column, so filtering locally searched 50 of 307 and
     * quietly told the rep there were no matches.
     */
    /** Re-load the board for the current filters, at most once per 400ms. */
    reloadBoardDebounced() {
      clearTimeout(this._boardReloadTimer)
      this._boardReloadTimer = setTimeout(() => {
        this.selectedCardIds = []
        this.dealsList = []
        this.initData()
      }, 400)
    },

    boardFilterParams() {
      const p = {}
      const f = this.filters || {}
      if (this.searchQuery) p.search = this.searchQuery
      // "Only mine" prefers the user id: owner NAMES drift (a rep marries, HR edits
       // the spelling) and a name-based filter would then quietly return nothing.
      if (this.onlyMine) {
        if (this.access.id) p.ownerId = this.access.id
        else if (this.currentUserName) p.owners = this.currentUserName
      }
      if (f.owners && f.owners.length) {
        // "Only mine" and an explicit owner filter would fight each other; the
        // narrower of the two wins, and "only mine" is always the narrower.
        if (!p.owners) p.owners = f.owners.join(',')
      }
      if (f.priorities && f.priorities.length) p.priorities = f.priorities.join(',')
      if (f.labels && f.labels.length) p.labels = f.labels.join(',')
      if (f.stages && f.stages.length) p.stages = f.stages.join(',')
      if (this.clientIdFilter) p.clientId = this.clientIdFilter
      return p
    },

    async fetchStagePage(pipeline, stageId, offset = 0) {
      const res = await axios.get(`crm/${pipeline}`, {
        params: {
          stage: stageId, limit: this.stagePageSize, offset, ...this.boardFilterParams(),
        },
        timeout: this.requestTimeout
      })
      const body = decryptData(res.data.payload)
      return (body && body.success && Array.isArray(body.data)) ? body.data : []
    },

    /** Legacy path: fetch the whole pipeline in one request (pre-pagination). */
    /**
     * THE TABLE MUST SHOW EVERYTHING, NOT THE BOARD'S WINDOW.
     *
     * Karim, 2 Sep: "the count is not working — there are 300 in the above menu,
     * and if you filter by table there is only 102."
     *
     * Both numbers were right, which is the worst kind of wrong. The kanban header
     * shows the SERVER's per-stage totals; the table was handed only the cards the
     * board had loaded — 50 per stage. A kanban column can honestly say "50 of 300"
     * because you scroll it. A flat table saying "102" is simply a false total.
     *
     * So switching to the table loads the whole filtered set in one request.
     */
    async switchToList() {
      if (this.viewMode === 'list') return
      this.viewMode = 'list'

      this.loading = true
      try {
        const all = await this.fetchWholePipeline(this.activePipeline)
        if (Array.isArray(all) && all.length) {
          if (this.activePipeline === 'renewals') this.renewalsList = all
          else if (this.activePipeline === 'tickets') this.ticketsList = all
          else this.dealsList = all
        }
      } catch (e) {
        // Keep whatever is already loaded rather than emptying the screen.
        this.apiError = 'Could not load the full list — showing what is already loaded.'
      } finally {
        this.loading = false
      }
    },

    async fetchWholePipeline(pipeline) {
      const res = await axios.get(`crm/${pipeline}`, {
        // Without the filters, the table would show every card while the header
        // counted only the filtered ones — the two would disagree on screen.
        params: this.boardFilterParams(),
        timeout: this.requestTimeout,
      })
      const body = decryptData(res.data.payload)
      return (body && body.success && Array.isArray(body.data)) ? body.data : []
    },

    mergeCards(list, incoming) {
      incoming.forEach(card => {
        if (!list.some(c => c.id === card.id)) list.push(card)
      })
    },

    /**
     * Load a board. Uses per-stage windows when the server exposes counts,
     * otherwise falls back to one unfiltered request so the board is NEVER
     * empty just because the backend is a version behind.
     * Stage keys come from the counts payload (real DB values) unioned with the
     * configured stages, so a card whose stage is not in our list still loads.
     */
    async loadPipelineCards(pipeline, configuredStages) {
      const counts = await this.fetchStageCounts(pipeline)

      if (!counts || !Object.keys(counts).length) {
        return this.fetchWholePipeline(pipeline)
      }

      const stageIds = Array.from(new Set([
        ...Object.keys(counts),
        ...configuredStages.map(s => s.id)
      ]))
      const pages = await Promise.all(
        stageIds.map(id => this.fetchStagePage(pipeline, id, 0).catch(() => []))
      )
      const merged = []
      pages.forEach(page => this.mergeCards(merged, page))

      // Safety net: counts said there is data but the windows came back empty
      // (e.g. stage values drifted) — do not show an empty board, load it whole.
      if (!merged.length) {
        return this.fetchWholePipeline(pipeline)
      }
      return merged
    },

    async fetchBackendRenewals(forceRefresh = false) {
      try {
        // Clear local array to avoid duplicate injection during forced resync
        this.renewalsList = []
        this.renewalsList = await this.loadPipelineCards('renewals', this.renewalStages)
      } catch (err) {
        console.error('Error in fetchBackendRenewals:', err)
        this.apiError = 'Could not load the renewals board. Please retry.'
      }
    },

    async fetchBackendDeals() {
      try {
        const cards = await this.loadPipelineCards('deals', this.dealStages)
        this.mergeCards(this.dealsList, cards)
      } catch (err) {
        console.error('Error in fetchBackendDeals:', err)
      }
    },

    /**
     * Audit C-1: the server verified (or refused) a certificate number. Trust the
     * server's card state — a certificate that did not match must NOT appear Won.
     */
    async handleAssigneeAdd({ cardId, userId, userName }) {
      try {
        const res = await axios.post(`crm/${this.activePipeline}/${cardId}/assignees`, { userId, userName })
        const body = decryptData(res.data.payload)
        this.handleCardRefreshed({ cardId, card: (body && body.data) || {} })
      } catch (e) {
        this.$toast({
          component: ToastificationContent,
          props: { title: 'Not added', icon: 'AlertCircleIcon', variant: 'danger', text: 'Could not add that assignee.' },
        })
      }
    },
    async handleAssigneeRemove({ cardId, userId }) {
      try {
        const res = await axios.delete(`crm/${this.activePipeline}/${cardId}/assignees/${userId}`)
        const body = decryptData(res.data.payload)
        this.handleCardRefreshed({ cardId, card: (body && body.data) || {} })
      } catch (e) {
        this.$toast({
          component: ToastificationContent,
          props: { title: 'Not removed', icon: 'AlertCircleIcon', variant: 'danger', text: 'Could not remove that assignee.' },
        })
      }
    },

    // Karim, 7 Sep: shared by the drawer's attachments and assignees actions
    // — both just need the card merged back in place after a server round trip.
    handleCardRefreshed({ cardId, card }) {
      const list = this.activeCardList
      const idx = list.findIndex(c => c.id === cardId)
      if (idx > -1 && card && card.id) {
        this.$set(list, idx, { ...list[idx], ...card })
        if (this.selectedCard && this.selectedCard.id === cardId) this.selectedCard = list[idx]
      }
    },

    handleCertificateLinked({ cardId, card, status }) {
      const list = this.activeCardList
      const idx = list.findIndex(c => c.id === cardId)
      if (idx > -1 && card && card.id) {
        const prevStage = list[idx].stage
        this.$set(list, idx, { ...list[idx], ...card })
        if (this.selectedCard && this.selectedCard.id === cardId) this.selectedCard = list[idx]

        const counts = this.stageCounts[this.activePipeline]
        if (counts && card.stage && card.stage !== prevStage) {
          if (counts[prevStage] !== undefined) this.$set(counts, prevStage, Math.max(0, Number(counts[prevStage]) - 1))
          if (counts[card.stage] !== undefined) this.$set(counts, card.stage, Number(counts[card.stage]) + 1)
        }
      }

      const ok = status === 'matched'
      this.$toast({
        component: ToastificationContent,
        props: {
          title: ok ? 'Certificate verified' : `Certificate ${status}`,
          icon: ok ? 'CheckCircleIcon' : 'AlertTriangleIcon',
          variant: ok ? 'success' : (status === 'mismatch' ? 'danger' : 'info'),
          text: ok
            ? 'Matched in the ERP — the opportunity is closed as Won.'
            : 'Stored, but the opportunity was NOT closed as Won.'
        }
      })
    },

    async loadMoreStage(stageId) {
      const pipeline = this.activePipeline
      const list = this.activeCardList
      const offset = list.filter(c => c.stage === stageId).length
      this.$set(this.stageLoading, stageId, true)
      try {
        const page = await this.fetchStagePage(pipeline, stageId, offset)
        this.mergeCards(list, page)
      } catch (e) {
        this.$toast({
          component: ToastificationContent,
          props: { title: 'Could not load more', icon: 'AlertCircleIcon', variant: 'danger', text: 'Please retry.' }
        })
      } finally {
        this.$set(this.stageLoading, stageId, false)
      }
    },

    getPendingCount(pipelineId) {
      // Audit A-1: prefer server totals — the loaded lists are windowed now.
      const counts = this.stageCounts[pipelineId]
      if (counts && Object.keys(counts).length) {
        const excluded = pipelineId === 'renewals'
          ? ['renewed', 'not_renewed']
          : (pipelineId === 'tickets' ? ['resolved', 'closed'] : ['won', 'rejected', 'lost'])
        return Object.keys(counts).reduce(
          (sum, stage) => (excluded.includes(stage) ? sum : sum + Number(counts[stage] || 0)), 0
        )
      }
      if (pipelineId === 'renewals') return this.renewalsList.filter(c => c.stage !== 'renewed' && c.stage !== 'not_renewed').length
      if (pipelineId === 'tickets') return this.ticketsList.filter(c => c.stage !== 'resolved' && c.stage !== 'closed').length
      return this.dealsList.filter(c => c.stage !== 'won' && c.stage !== 'rejected' && c.stage !== 'lost').length
    },

    // Drag-and-Drop & Stage Movement Handlers
    handleCardMoved({ card, toStage }) {
      this.initiateCardMove(card, toStage)
    },

    handleStageSelectChange({ card, newStage }) {
      this.initiateCardMove(card, newStage)
    },

    initiateCardMove(card, targetStage) {
      const prevStage = card.stage
      if (this.activePipeline === 'renewals' && (targetStage === 'renewed' || targetStage === 'not_renewed')) {
        this.decisionCard = card
        this.showDecisionModal = true
        return
      }

      // Open Activity Modal to prompt for activity completion & next activity scheduling on stage move
      this.activityCard = card
      this.pendingTargetStage = targetStage
      this.startInEmailMode = false
      this.showActivityModal = true
    },

    async submitActivityModal({ outcomeNote, nextActivity, targetStage, lostReason, requestId }) {
      const card = this.activityCard
      if (!card) return

      // Log previous activity completion if present
      if (card.nextActivity) {
        if (!card.activities) this.$set(card, 'activities', [])
        card.activities.unshift({
          id: `act-${Date.now()}`,
          type: card.nextActivity.type,
          status: 'Done',
          dateCompleted: new Date().toISOString().split('T')[0],
          note: outcomeNote || card.nextActivity.note,
          user: this.currentUserName
        })
        this.logCardHistory(card, `Completed ${card.nextActivity.type} Activity: "${outcomeNote || card.nextActivity.note || 'Done'}"`)
      }

      // Assign new Next Activity — Karim, 7 Sep: "button for none or no next
      // activity." nextActivity is null when the rep picked "None".
      this.$set(card, 'nextActivity', nextActivity)
      this.logCardHistory(card, nextActivity
        ? `Scheduled Next Activity: ${nextActivity.type} due ${nextActivity.dueDate}`
        : 'No next activity scheduled')

      // Apply stage move if targetStage is provided
      if (targetStage && card.stage !== targetStage) {
        const prevStage = card.stage
        // Audit A-1: keep the per-stage server totals honest on moves.
        const counts = this.stageCounts[this.activePipeline]
        if (counts) {
          if (counts[prevStage] !== undefined) this.$set(counts, prevStage, Math.max(0, Number(counts[prevStage]) - 1))
          if (counts[targetStage] !== undefined) this.$set(counts, targetStage, Number(counts[targetStage]) + 1)
        }
        card.stage = targetStage
        card.stageChangedAt = new Date().toISOString()
        // Entering a stage starts at its first sub-step
        const stageDef = this.activeStages.find(s => s.id === targetStage)
        const firstSub = stageDef && stageDef.subStages && stageDef.subStages.length
          ? stageDef.subStages[0]
          : null
        this.$set(card, 'subStage', firstSub)
        this.logCardHistory(card, targetStage === 'lost' && lostReason
          ? `Marked Lost — ${lostReason}`
          : targetStage === 'won' && requestId
            ? `Marked Won — application #${requestId}`
            : `Moved from '${prevStage}' to '${targetStage}'`)
      }

      this.saveLocalState()
      this.closeActivityModal()

      // IMPORTANT: this single call both logs the completed activity and stores the
      // new one. It must not race with a card save, or the completed activity is
      // overwritten before the server can log it.
      try {
        const res = await axios.post('crm/activities', {
          cardId: card.id,
          pipeline: this.activePipeline,
          outcomeNote,
          nextActivity,
          targetStage,
          subStage: card.subStage,
          user: this.currentUserName,
          lostReason,
          requestId
        })
        // Re-sync the card from the server so the board can never drift from the DB.
        const body = decryptData(res.data.payload)
        if (body && body.success && body.data) {
          const list = this.activeCardList
          const idx = list.findIndex(c => c.id === body.data.id)
          if (idx > -1) this.$set(list, idx, { ...list[idx], ...body.data })
        }
        this.$toast({
          component: ToastificationContent,
          props: {
            title: targetStage === 'lost' ? 'Marked Lost' : targetStage === 'won' ? 'Marked Won' : (nextActivity ? 'Activity Scheduled' : 'Activity Logged'),
            icon: targetStage === 'lost' ? 'XCircleIcon' : 'CheckIcon',
            variant: targetStage === 'lost' ? 'danger' : 'success',
            text: targetStage === 'lost' ? lostReason
              : targetStage === 'won' ? 'Opportunity won'
              : (nextActivity ? `Next activity: ${nextActivity.type} set` : 'No next activity scheduled'),
          },
        })
      } catch (e) {
        this.$toast({
          component: ToastificationContent,
          props: { title: 'Not saved', icon: 'AlertCircleIcon', variant: 'danger', text: 'The activity could not be saved. Please retry.' }
        })
      }
    },

    closeActivityModal() {
      this.showActivityModal = false
      this.activityCard = null
      this.pendingTargetStage = null
      this.startInEmailMode = false
    },

    /**
     * Karim, 7 Sep: "sales can be able to upload attachment with email send
     * when on activity select email... email will be send on date selected
     * there should be also a send now option." One endpoint (crm/scheduled-
     * emails) handles both send-now and send-later; CrmMailService on the
     * backend does the actual Gmail-API send + auto signature + activity log.
     */
    async submitCardEmail({ to, cc, subject, body, sendAt, attachments }) {
      const card = this.activityCard
      if (!card) return

      const form = new FormData()
      form.append('contextType', 'card')
      form.append('pipeline', this.activePipeline)
      form.append('cardId', card.id)
      to.forEach(addr => form.append('to[]', addr))
      cc.forEach(addr => form.append('cc[]', addr))
      form.append('subject', subject)
      form.append('body', body)
      if (sendAt) form.append('sendAt', sendAt)
      attachments.forEach(file => form.append('attachments[]', file))

      try {
        const res = await axios.post('crm/scheduled-emails', form, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })
        const responseBody = decryptData(res.data.payload)
        const scheduled = responseBody && responseBody.data && responseBody.data.status === 'pending'
        this.closeActivityModal()
        this.$toast({
          component: ToastificationContent,
          props: {
            title: scheduled ? 'Email scheduled' : 'Email sent',
            icon: 'MailIcon',
            variant: 'success',
            text: scheduled ? `Will send on ${sendAt}` : `Sent to ${to.join(', ')}`
          }
        })
      } catch (e) {
        this.$toast({
          component: ToastificationContent,
          props: { title: 'Not sent', icon: 'AlertCircleIcon', variant: 'danger', text: (e.response && e.response.data && e.response.data.message) || 'The email could not be sent.' }
        })
      }
    },

    submitDecision({ status, reason }) {
      if (!this.decisionCard) return
      const card = this.decisionCard
      this.$set(card, 'decision', { status, reason, time: new Date().toISOString() })
      card.stage = status
      card.stageChangedAt = new Date().toISOString()
      this.logCardHistory(card, `Completed Renewal: [${status}] - Reason: ${reason}`)

      this.showDecisionModal = false
      this.decisionCard = null
      this.$toast({
        component: ToastificationContent,
        props: { title: 'Outcome Logged', icon: 'CheckIcon', variant: 'success', text: `Status set to ${status}` }
      })
      // Persist the renew/not-renew decision.
      this.persistCard(card, this.activePipeline)
    },

    cancelDecision() {
      this.showDecisionModal = false
      this.decisionCard = null
    },

    logCardHistory(card, actionText) {
      if (!card.history) this.$set(card, 'history', [])
      const now = new Date()
      card.history.unshift({
        time: now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) + ' ' + now.toLocaleDateString(),
        action: actionText,
        user: this.currentUserName
      })
      this.saveLocalState()
    },

    // Card Details Drawer & Inline Edits
    openDetail(card) {
      this.selectedCard = card
    },

    // Karim, 8 Sep: "I can select the product any time and it automatically
    // adds the value. And auto select 'quotation'." Never moves a card
    // BACKWARD (a Won/Lost/Quotation card picking a different product
    // doesn't get bumped back to an earlier stage).
    handleProductUpdate({ cardId, serviceScheme, productId, value, currency }) {
      const card = this.activeCardList.find(c => c.id === cardId)
      if (!card) return
      this.$set(card, 'serviceScheme', serviceScheme)
      this.$set(card, 'productId', productId)
      if (value !== undefined && value !== null) this.$set(card, 'value', value)
      if (currency) this.$set(card, 'currency', currency)

      const EARLY_STAGES = ['new', 'proposal', 'responded', 'negotiation']
      if (EARLY_STAGES.includes(card.stage)) {
        this.$set(card, 'stage', 'quotation')
      }
      this.logCardHistory(card, `Product set to ${serviceScheme}`)
      this.persistCard(card, this.activePipeline)
    },

    handleFieldUpdate({ cardId, field, value }) {
      const card = this.activeCardList.find(c => c.id === cardId)
      if (card) {
        this.$set(card, field, value)
        this.logCardHistory(card, `Updated field '${field}'`)
        // Audit P0-1b: persist — otherwise the edit is lost on refresh.
        this.persistCard(card, this.activePipeline)
      }
    },

    async handleCommentAdd({ cardId, text }) {
      const card = this.activeCardList.find(c => c.id === cardId)
      if (!card) return
      if (!card.comments) this.$set(card, 'comments', [])
      card.comments.unshift({ user: this.currentUserName, text, time: 'Just now' })
      this.logCardHistory(card, `Added comment: "${text.substring(0, 25)}..."`)
      // Persist the comment — otherwise it evaporates on refresh.
      try {
        await axios.post(`crm/${this.activePipeline}/${card.id}/comment`, { text })
      } catch (e) {
        this.$toast({
          component: ToastificationContent,
          props: { title: 'Comment not saved', icon: 'AlertCircleIcon', variant: 'danger', text: 'Please retry — the server did not store it.' }
        })
      }
    },

    // MID-CRM P1.5 — removed handleChecklistAdd / Toggle / Delete and
    // handleTimeEntryAdd. They only mutated the local card object: there is no
    // checklist or time-entry column on crm_cards and no table behind them, so every
    // tick and every logged hour was lost on refresh. The tabs are hidden; the
    // handlers are gone. Build the schema first if these are ever wanted.

    handleLabelAdd({ cardId, label }) {
      const card = this.activeCardList.find(c => c.id === cardId)
      if (card) {
        if (!card.labels) this.$set(card, 'labels', [])
        if (!card.labels.some(l => l.name === label.name)) {
          card.labels.push(label)
          this.logCardHistory(card, `Added label '${label.name}'`)
          // Audit P0-1c: persist labels.
          this.persistCard(card, this.activePipeline)
        }
      }
    },

    handleLabelRemove({ cardId, labelIndex }) {
      const card = this.activeCardList.find(c => c.id === cardId)
      if (card && card.labels && card.labels[labelIndex]) {
        const removed = card.labels[labelIndex].name
        card.labels.splice(labelIndex, 1)
        this.logCardHistory(card, `Removed label '${removed}'`)
        // Audit P0-1c: persist label removal.
        this.persistCard(card, this.activePipeline)
      }
    },

    // MID-CRM P1.4 — removed handleWatcherToggle. `crm_cards` has no watchers
    // column, so "X is now watching this card" was written to the local object and
    // to nothing else.

    /**
     * MID-CRM P2.1 — hand the opportunity to a real salesperson.
     * The server is the authority on who may reassign (managers only); we send the
     * change and re-sync the card from the response so the board cannot drift.
     */
    async handleOwnerChange({ cardId, userId, userName }) {
      const card = this.activeCardList.find(c => c.id === cardId)
      if (!card) return

      const prevOwner = card.owner
      const prevOwnerId = card.ownerId
      this.$set(card, 'owner', userName)
      this.$set(card, 'ownerId', userId)
      this.logCardHistory(card, `Assigned to ${userName}`)

      try {
        const res = await axios.post(`crm/${this.activePipeline}/reassign`, {
          cardIds: [cardId],
          ownerId: userId
        })
        const body = decryptData(res.data.payload)
        if (!body || !body.success) throw new Error((body && body.message) || 'Reassign refused')
        this.$toast({
          component: ToastificationContent,
          props: { title: 'Assigned', icon: 'UserCheckIcon', variant: 'success', text: `${card.title} → ${userName}` }
        })
      } catch (e) {
        // Put it back: showing an owner the server rejected is exactly the kind of
        // lie this cleanup is removing.
        this.$set(card, 'owner', prevOwner)
        this.$set(card, 'ownerId', prevOwnerId)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Not assigned',
            icon: 'AlertCircleIcon',
            variant: 'danger',
            text: 'The server refused the change — managers only.'
          }
        })
      }
    },

    // Add Card Operation
    submitAddCard(formData) {
      // Karim, 7 Sep: "pull items from the leads, not the customers." A New
      // Deal now always comes from a lead (CardAddModal enforces leadId is
      // set for the deals pipeline) — convert it through the real endpoint
      // instead of fabricating a random-id local card and hoping the
      // fire-and-forget POST below matches it. buildOpportunity() computes
      // its own deterministic id (D-L{leadId}-{scheme}), so the optimistic
      // local id here would never have matched the server's anyway.
      if (this.activePipeline === 'deals' && formData.leadId) {
        this.convertLeadToOpportunity(formData)
        return
      }

      const prefixMap = { renewals: 'R', tickets: 'T', deals: 'D' }
      const prefix = prefixMap[this.activePipeline] || 'D'
      const id = `${prefix}-${Math.floor(1000 + Math.random() * 9000)}`
      const defaultStage = this.activeStages[0].id
      const firstSub = (this.activeStages[0].subStages || [])[0] || null

      const card = {
        id,
        title: formData.title,
        client: formData.client,
        stage: defaultStage,
        owner: this.currentUserName,
        description: formData.description,
        date: new Date().toISOString().split('T')[0],
        priority: formData.priority || 'Medium',
        dueDate: formData.dueDate || null,
        comments: [],
        history: [{ time: 'Just now', action: 'Created item', user: this.currentUserName }]
      }

      // Every card here IS an opportunity for a client — carry the product/service.
      card.subStage = firstSub
      card.type = formData.type || (this.activePipeline === 'renewals' ? 'renewal' : 'new')
      card.serviceScheme = formData.serviceScheme || null
      card.productId = formData.productId || null
      card.erpCertificateId = formData.erpCertificateId || null
      card.currency = formData.currency || 'AED'
      card.value = formData.value || 0
      card.clientId = formData.clientId || null
      card.companyId = formData.companyId || null
      // Assignee chosen in the modal (a real user), else the current user.
      if (formData.ownerId) {
        card.ownerId = formData.ownerId
        card.owner = formData.owner || this.currentUserName
      }
      card.expectedCloseDate = formData.expectedCloseDate || null
      // Ledger G-13 — BRD §3.2 required Deal fields. These must be forwarded or the
      // columns stay NULL for any card created on the board (source/campaign feed the
      // §7.1 dashboards, CR Number is the facility identity, isBroker drives §4.2).
      card.campaign = formData.campaign || null
      card.campaignId = formData.campaignId || null
      card.source = formData.source || null
      card.crNumber = formData.crNumber || null
      card.isBroker = (formData.isBroker === null || formData.isBroker === undefined)
        ? null
        : !!formData.isBroker

      if (this.activePipeline === 'renewals') {
        card.remainingDays = formData.remainingDays || 30
        this.renewalsList.unshift(card)
      } else if (this.activePipeline === 'tickets') {
        this.ticketsList.unshift(card)
      } else {
        this.dealsList.unshift(card)
      }

      // Persist to the CRM backend (opportunities/renewals/deals/tickets)
      this.persistCard(card, this.activePipeline)

      this.showAddModal = false
      this.$toast({
        component: ToastificationContent,
        props: { title: 'Item Created', icon: 'CheckIcon', variant: 'success', text: `Added #${id}` }
      })
    },

    /**
     * Karim, 7 Sep: New Deals now always start from a lead. Calls the real
     * conversion endpoint (CrmLeadController::convert -> buildOpportunity) —
     * that is also what already back-fills campaign/source/CR number/broker
     * from the lead when the rep leaves them blank, and marks the lead
     * converted_card_id so it drops out of the "still no opportunity" picker.
     */
    async convertLeadToOpportunity(formData) {
      try {
        const res = await axios.post(`crm/leads/${formData.leadId}/convert`, {
          serviceScheme: formData.serviceScheme,
          serviceId: formData.serviceId,
          productId: formData.productId,
          type: formData.type,
          value: formData.value,
          currency: formData.currency,
          expectedCloseDate: formData.expectedCloseDate,
          priority: formData.priority,
          campaign: formData.campaign,
          campaignId: formData.campaignId,
          source: formData.source,
          crNumber: formData.crNumber,
          isBroker: formData.isBroker,
          description: formData.description,
        })
        const body = decryptData(res.data.payload)
        const card = body && body.data && body.data.card
        if (card) {
          // Same card might already be on the board (re-converting an
          // existing opportunity) — replace, don't duplicate.
          const idx = this.dealsList.findIndex(c => c.id === card.id)
          if (idx === -1) this.dealsList.unshift(card)
          else this.dealsList.splice(idx, 1, card)
        }
        this.showAddModal = false
        this.$toast({
          component: ToastificationContent,
          props: { title: 'Opportunity created', icon: 'CheckIcon', variant: 'success', text: card ? `${card.title}` : 'Done' },
        })
      } catch (e) {
        this.$toast({
          component: ToastificationContent,
          props: { title: 'Failed', icon: 'AlertCircleIcon', variant: 'danger', text: 'Could not create the opportunity from this lead' },
        })
      }
    },

    deleteCard(card) {
      this.$swal({
        title: 'Delete pipeline item?',
        text: 'Permanently removes item from storage.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Delete',
        customClass: { confirmButton: 'btn btn-danger', cancelButton: 'btn btn-outline-secondary ml-1' }
      }).then(async res => {
        if (res.isConfirmed) {
          // Audit P0-1e: delete on the server first — otherwise the card
          // reappears on refresh.
          try {
            await axios.delete(`crm/${this.activePipeline}/${card.id}`)
          } catch (e) {
            this.$toast({
              component: ToastificationContent,
              props: { title: 'Delete failed', icon: 'AlertCircleIcon', variant: 'danger', text: 'The server did not delete the card. Please retry.' }
            })
            return
          }
          if (this.activePipeline === 'renewals') this.renewalsList = this.renewalsList.filter(c => c.id !== card.id)
          else if (this.activePipeline === 'tickets') this.ticketsList = this.ticketsList.filter(c => c.id !== card.id)
          else this.dealsList = this.dealsList.filter(c => c.id !== card.id)

          this.selectedCard = null
          this.selectedCardIds = this.selectedCardIds.filter(id => id !== card.id)
        }
      })
    },

    // Quick Action Menu Handlers
    handleQuickAction({ card, action }) {
      if (action === 'delete') this.deleteCard(card)
      else if (action === 'assign') this.openDetail(card)
      else if (action === 'schedule-activity') {
        this.activityCard = card
        this.pendingTargetStage = null
        this.startInEmailMode = false
        this.showActivityModal = true
      } else if (action === 'email') {
        this.activityCard = card
        this.pendingTargetStage = null
        this.startInEmailMode = true
        this.showActivityModal = true
      } else if (action === 'priority') {
        const nextP = card.priority === 'High' ? 'Low' : (card.priority === 'Low' ? 'Medium' : 'High')
        this.$set(card, 'priority', nextP)
        this.persistCard(card, this.activePipeline)
      } else if (action === 'label') {
        // MID-CRM P1.1: this used to push a RANDOM label
        // (DEFAULT_LABELS[Math.floor(Math.random() * …)]) — the "fake labels" that
        // appeared on cards nobody tagged. Open the card so the rep picks a real one.
        this.openDetail(card)
      } else if (action === 'reminder') {
        this.toggleCardFlag(card, 'reminder', 'reminder')
      } else if (action === 'needs-update') {
        this.toggleCardFlag(card, 'needsUpdate', 'needsUpdate')
      } else if (action === 'concern') {
        this.toggleConcernLabel(card)
      }
    },

    /**
     * Karim, 7 Sep: Reminder / Needs-Update quick-actions — a plain on/off
     * toggle via the same bulk-action endpoint the toolbar uses (cardIds
     * narrowed to just this one card), so it gets the same ownership check
     * and history entry as every other card mutation, not a bespoke route.
     */
    async toggleCardFlag(card, localField, backendAction) {
      const next = !card[localField]
      this.$set(card, localField, next)
      try {
        await axios.post(`crm/${this.activePipeline}/bulk-action`, {
          cardIds: [card.id],
          action: backendAction,
          value: next,
        })
      } catch (e) {
        this.$set(card, localField, !next)
        this.$toast({
          component: ToastificationContent,
          props: { title: 'Not saved', icon: 'AlertCircleIcon', variant: 'danger', text: 'Could not update the flag.' },
        })
      }
    },

    // Karim, 7 Sep: "concern, which means the client have concern." Reuses
    // the existing labels mechanism (same addLabel/removeLabel + persistCard
    // pattern already used elsewhere on this board).
    toggleConcernLabel(card) {
      const labels = card.labels || []
      const has = labels.some(l => l.name === 'Concern')
      const next = has ? labels.filter(l => l.name !== 'Concern') : [...labels, CONCERN_LABEL]
      this.$set(card, 'labels', next)
      this.persistCard(card, this.activePipeline)
    },

    // Stage Manager operations (act on the active editable pipeline:
    // opportunities or deals — including each stage's sub-steps)
    setEditableStages(stages) {
      this.dealStages = stages
    },
    currentCardsForStages() {
      return this.dealsList
    },
    async persistStages() {
      try {
        await axios.post('crm/stages', {
          pipeline: 'deals',
          stages: this.dealStages
        })
      } catch (e) {
        // non-blocking
      }
    },

    createStage({ name, color }) {
      const id = name.toLowerCase().replace(/[^a-z0-9]/g, '-')
      const list = this.editableStages
      if (!list.some(s => s.id === id)) {
        this.setEditableStages([...list, { id, name, color, wipLimit: 0, description: '', subStages: [] }])
        this.persistStages()
      }
    },

    deleteStage(stageId) {
      const list = this.editableStages
      if (list.length <= 1) return
      const fallback = list.find(s => s.id !== stageId).id
      this.currentCardsForStages().forEach(d => { if (d.stage === stageId) d.stage = fallback })
      this.setEditableStages(list.filter(s => s.id !== stageId))
      this.persistStages()
    },

    updateStage({ stageId, field, value }) {
      const st = this.editableStages.find(s => s.id === stageId)
      if (st) {
        this.$set(st, field, value)
        this.persistStages()
      }
    },

    reorderStage({ index, direction }) {
      const list = [...this.editableStages]
      const target = direction === 'up' ? index - 1 : index + 1
      if (target >= 0 && target < list.length) {
        const temp = list[index]
        list[index] = list[target]
        list[target] = temp
        this.setEditableStages(list)
        this.persistStages()
      }
    },

    handleStageReorder({ stages }) {
      this.setEditableStages([...stages])
      this.persistStages()
    },

    // Bulk selection & actions
    toggleCardSelect(card) {
      const idx = this.selectedCardIds.indexOf(card.id)
      if (idx > -1) this.selectedCardIds.splice(idx, 1)
      else this.selectedCardIds.push(card.id)
    },

    selectAllCards(cards) {
      this.selectedCardIds = cards.map(c => c.id)
    },

    handleBulkAction({ action, data }) {
      if (action === 'clear') {
        this.selectedCardIds = []
      } else if (action === 'delete') {
        this.activeCardList.forEach(card => {
          if (this.selectedCardIds.includes(card.id)) {
            if (this.activePipeline === 'renewals') this.renewalsList = this.renewalsList.filter(c => c.id !== card.id)
            else if (this.activePipeline === 'tickets') this.ticketsList = this.ticketsList.filter(c => c.id !== card.id)
            else this.dealsList = this.dealsList.filter(c => c.id !== card.id)
          }
        })
        this.selectedCardIds = []
      } else if (action === 'move' && data) {
        this.filteredCards.forEach(card => {
          if (this.selectedCardIds.includes(card.id)) {
            card.stage = data
          }
        })
        this.selectedCardIds = []
      }
    },

    // Advanced Filter Handlers
    handleFilterChange({ key, value }) {
      this.$set(this.filters, key, value)
    },
    clearFilterKey(key) {
      this.$set(this.filters, key, [])
    },
    clearAllFilters() {
      this.filters = { ...DEFAULT_FILTERS }
      this.searchQuery = ''
      this.clearClientFilter()
    },
    clearClientFilter() {
      if (!this.clientIdFilter) return
      this.clientIdFilter = ''
      const query = { ...this.$route.query }
      delete query.clientId
      delete query.clientName
      this.$router.replace({ query }).catch(() => {})
      this.reloadBoardDebounced()
    },

    // Board Settings update
    updateBoardSetting({ key, value }) {
      this.$set(this.boardSettings, key, value)
    },

    // Data Export
    exportData(format) {
      const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(this.filteredCards, null, 2))
      const downloadAnchor = document.createElement('a')
      downloadAnchor.setAttribute("href", dataStr)
      downloadAnchor.setAttribute("download", `crm_${this.activePipeline}_export.${format}`)
      document.body.appendChild(downloadAnchor)
      downloadAnchor.click()
      downloadAnchor.remove()
    }
  }
}
</script>

<style lang="scss" scoped>
$primary: #2563eb;
$slate-50: #f8fafc;
$slate-100: #f1f5f9;
$slate-200: #e2e8f0;
$slate-400: #94a3b8;
$slate-500: #64748b;
$slate-700: #334155;
$slate-900: #0f172a;

.crm-client-filter-banner {
  display: flex;
  align-items: center;
  gap: .5rem;
  background: rgba(37, 99, 235, .08);
  border: 1px solid rgba(37, 99, 235, .25);
  border-radius: .4rem;
  padding: .5rem .8rem;
  margin: 0 0 .75rem;
  font-size: .82rem;
  color: $slate-700;
}
.crm-client-filter-banner__clear {
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: .25rem;
  border: none;
  background: none;
  color: $primary;
  font-weight: 700;
  font-size: .78rem;
  cursor: pointer;

  &:hover { text-decoration: underline; }
}

.crm-workspace {
  background-color: $slate-50;
  min-height: 100vh;
  font-family: 'Outfit', 'Inter', sans-serif;
  color: $slate-700;
  padding-bottom: 2rem;
}

.crm-header {
  position: relative;
  background: linear-gradient(135deg, $slate-900, darken($slate-900, 5%));
  color: #ffffff;
  padding: 1.5rem 2rem 0 2rem;
  overflow: hidden;

  &__glow {
    position: absolute; top: -50px; right: -50px;
    width: 300px; height: 300px;
    background: radial-gradient(circle, rgba($primary, 0.15) 0%, rgba(0,0,0,0) 70%);
  }

  &__content {
    display: flex; justify-content: space-between; align-items: center;
    flex-wrap: wrap; position: relative; z-index: 2;
  }

  &__icon {
    width: 48px; height: 48px; border-radius: 12px;
    background: rgba($primary, 0.2); border: 1px solid rgba($primary, 0.3);
    display: flex; align-items: center; justify-content: center;
    color: lighten($primary, 10%);
  }

  &__title { font-size: 1.4rem; font-weight: 900; color: #ffffff; margin: 0; }
  &__subtitle { font-size: 0.82rem; color: $slate-400; margin: 0.2rem 0 0 0; }
}

.crm-nav {
  display: flex; margin-top: 1.25rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);

  &__tab {
    background: transparent; border: none; border-bottom: 3px solid transparent;
    padding: 0.7rem 1.1rem; color: $slate-400; font-weight: 700; font-size: 0.85rem;
    display: flex; align-items: center; cursor: pointer;

    &--active { color: #ffffff; border-bottom-color: $primary; background: rgba($primary, 0.08); }
  }

  &__badge {
    margin-left: 0.4rem; font-size: 0.72rem; font-weight: 800; padding: 0.1rem 0.4rem; border-radius: 20px;
    &--warning { background: rgba(#2563eb, 0.2); color: #2563eb; }
    &--primary { background: rgba($primary, 0.2); color: lighten($primary, 15%); }
    &--success { background: rgba(#10b981, 0.2); color: #10b981; }
  }
}

.crm-view-switch {
  display: inline-flex; background: rgba(255, 255, 255, 0.05); border-radius: 8px; padding: 2px;
  &__btn {
    border: none; background: transparent; color: $slate-400; font-weight: 700; font-size: 0.78rem;
    padding: 0.3rem 0.65rem; border-radius: 6px; display: flex; align-items: center; gap: 0.3rem; cursor: pointer;
    &--active { background: #ffffff; color: $slate-900; }
  }
}

.crm-btn {
  display: inline-flex; align-items: center; gap: 0.35rem; border-radius: 8px;
  font-weight: 700; font-size: 0.78rem; padding: 0.45rem 0.85rem; border: 1px solid transparent; cursor: pointer;
  &--primary { background-color: $primary; color: #ffffff; }
  &--secondary { background-color: rgba(255, 255, 255, 0.08); color: #ffffff; }
  /* Outcome buttons in the selection bar. Outlined rather than filled: recording a
     loss is a normal, frequent action, not an alarm. */
  &--success { background: #fff; color: #28c76f; border-color: #28c76f; }
  &--danger { background: #fff; color: #ea5455; border-color: #ea5455; }
  &[disabled] { opacity: .55; cursor: not-allowed; }
}
.crm-reassign-bar {
  display: flex; align-items: center; gap: .5rem; flex-wrap: wrap;
  background: #fff; border: 1px solid #e4e4ec; border-left: 3px solid #2563eb;
  border-radius: .4rem; padding: .5rem .75rem;
}
.crm-reassign-bar__count { font-size: .8rem; font-weight: 700; color: #5e5873; display: inline-flex; align-items: center; }
/* Bulk bar is deliberately calmer than the selection bar — it is a bigger action,
   so it should read as a tool, not as an alert demanding to be clicked. */
.crm-reassign-bar__preview {
  font-size: .8rem; font-weight: 700; color: #1e40af;
  display: inline-flex; align-items: center;
}
.crm-reassign-bar__select {
  border: 1px solid #e4e4ec; border-radius: .3rem; padding: .3rem .5rem; font-size: .8rem; background: #fff; color: #5e5873;
}
.mr-05 { margin-right: .25rem; }
</style>
